import { Plus, Trash2 } from 'lucide-react'
import { DropdownSelect, type DropdownOption } from './ui/DropdownSelect'

export interface FilterCondition {
  field: string
  operator: string
  value: string
}

const OPERATORS = [
  { value: 'eq', label: 'Equals' },
  { value: 'neq', label: 'Not equals' },
  { value: 'contains', label: 'Contains' },
  { value: 'gt', label: 'Greater than' },
  { value: 'lt', label: 'Less than' },
  { value: 'is_null', label: 'Is empty' },
  { value: 'is_not_null', label: 'Is not empty' },
]

const OPERATOR_OPTIONS: DropdownOption[] = OPERATORS.map(operator => ({
  value: operator.value,
  label: operator.label,
}))

const LEAD_FIELDS = [
  { value: 'status', label: 'Status' },
  { value: 'source', label: 'Source' },
  { value: 'firstName', label: 'First Name' },
  { value: 'lastName', label: 'Last Name' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'organization', label: 'Organization' },
  { value: 'assignedTo', label: 'Assigned To' },
  { value: 'slaBreahed', label: 'SLA Breached' },
]

const DEAL_FIELDS = [
  { value: 'status', label: 'Stage' },
  { value: 'title', label: 'Title' },
  { value: 'value', label: 'Value' },
  { value: 'assignedTo', label: 'Assigned To' },
  { value: 'slaBreahed', label: 'SLA Breached' },
]

interface Props {
  filters: FilterCondition[]
  onChange: (filters: FilterCondition[]) => void
  refType?: 'lead' | 'deal'
}

export function FilterBuilder({ filters, onChange, refType = 'lead' }: Props) {
  const fields = refType === 'deal' ? DEAL_FIELDS : LEAD_FIELDS
  const fieldOptions: DropdownOption[] = fields.map(field => ({
    value: field.value,
    label: field.label,
  }))

  const add = () => onChange([...filters, { field: fields[0].value, operator: 'eq', value: '' }])
  const remove = (i: number) => onChange(filters.filter((_, idx) => idx !== i))
  const update = (i: number, patch: Partial<FilterCondition>) =>
    onChange(filters.map((f, idx) => idx === i ? { ...f, ...patch } : f))

  return (
    <div className="space-y-2">
      {filters.map((f, i) => (
        <div key={i} className="flex items-center gap-2">
          <DropdownSelect
            value={f.field}
            onChange={value => update(i, { field: value })}
            options={fieldOptions}
            className="flex-1"
          />
          <DropdownSelect
            value={f.operator}
            onChange={value => update(i, { operator: value })}
            options={OPERATOR_OPTIONS}
            className="w-40"
          />
          {!['is_null', 'is_not_null'].includes(f.operator) && (
            <input
              value={f.value}
              onChange={e => update(i, { value: e.target.value })}
              placeholder="Value"
              className="input flex-1"
            />
          )}
          <button onClick={() => remove(i)} className="btn-ghost btn p-1.5 text-red-500">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
      <button onClick={add} className="btn-ghost btn-sm btn text-primary-600">
        <Plus className="w-3.5 h-3.5" /> Add Filter
      </button>
    </div>
  )
}
